/**
 * No building, by CounselEAR clinic (2026-10-14).
 *
 * Isaiah Cotto, 2026-10-14: the CounselEAR clinic "cannot be missing and is
 * super important for scheduling". The residents eMerge created from
 * CounselEAR with no building are grouped by their Preferred Clinic -- which
 * is the facility they live at (Operations ruling, April Torino,
 * 2026-10-13) -- with the commonest building the clinic's other residents
 * already live at as a suggestion. "Set this clinic's building" confirms which
 * eMerge building the clinic is: a person's ratification, recorded with who
 * made it. Confirming places the clinic's residents who have no building
 * there, whatever created them (CounselEAR, Monday, a PA -- ruling, Isaiah
 * Cotto, 2026-10-14: "Confirming the clinic should place them."); their PA is
 * then the building's PA. A resident who already has a building is never
 * moved, nor one a person recorded another answer for in eMerge.
 *
 * The server decides who may confirm (COUNSELEAR_CLINIC_CONFIRM, enterprise),
 * and decides again on every request.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { noBuildingApi, type ClinicGroup, type ClinicGroups, type FacilityHit, type ConfirmResult } from './noBuildingApi';
import { refusalWords } from './resubmittalsApi';

const pct = (share: number | null | undefined) => (share === null || share === undefined ? '' : `${Math.round(share * 100)}%`);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** Those a confirmation places: the residents with no building recorded at all. */
const placeableOf = (g: ClinicGroup) => (typeof g.placeable === 'number' ? g.placeable : g.residents);

/** The clinic's name as a person reads it: CounselEAR's name, else its id. */
export function clinicLabel(g: Pick<ClinicGroup, 'clinic_id' | 'clinic_name'>): string {
  if (g.clinic_name) return g.clinic_name;
  if (g.clinic_id) return `Clinic ${g.clinic_id}`;
  return 'No clinic in CounselEAR';
}

function suggestionWords(g: ClinicGroup): string {
  const s = g.suggestion;
  if (!s) return 'None of this clinic\'s residents has a building yet.';
  return `${s.facility_name || 'A building'}: ${s.at_building} of the ${plural(s.placed, 'resident', 'residents')} of this clinic who have a building (${pct(s.share)})${s.buildings > 1 ? `, across ${s.buildings} buildings` : ''}.`;
}

type Choice = { id: string; name: string };

function SetBuilding({ g, onDone, onCancel, onStale }: { g: ClinicGroup; onDone: (r: ConfirmResult, building: Choice) => void; onCancel: () => void; onStale: (words: string) => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<FacilityHit[] | null>(null);
  const [searchProblem, setSearchProblem] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Choice | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setHits(null); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const r = await noBuildingApi.facilities(s);
        if (!live) return;
        if (r.success && r.data) { setHits(r.data.items); setSearchProblem(null); } else setSearchProblem(refusalWords(r, 'Could not search the buildings.'));
      } catch { if (live) setSearchProblem('Could not reach eMerge.'); }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  const confirm = async () => {
    if (!chosen || !g.clinic_id) return;
    setBusy(true); setProblem(null);
    try {
      // Confirming a standing proposal acts on that proposal only: if it changed, the server refuses (STALE).
      const expected = g.proposed && g.proposed.facility_id === chosen.id ? g.proposed.decision_id : null;
      const r = await noBuildingApi.setBuilding(g.clinic_id, chosen.id, {
        expectedProposalId: expected,
        // What this screen saw; the server refuses (409 STALE) if someone confirmed the clinic since.
        expectedRatifiedId: g.ratified ? g.ratified.decision_id : null,
        replace: changing,
      });
      if (r.success && r.data) onDone(r.data, chosen);
      else if (r.code === 'STALE' || r.code === 'RATIFIED_ELSEWHERE') {
        // Someone else confirmed this clinic since the screen was drawn: nothing was recorded; show what is true now.
        onStale(`${clinicLabel(g)}: someone confirmed this clinic's building since you opened it. Nothing was recorded. The list now shows its current building; look again before changing it.`);
      } else setProblem(refusalWords(r, 'The building was not set.'));
    } catch { setProblem('Could not reach eMerge. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const label = clinicLabel(g);
  // Choosing a building other than the clinic's confirmed one replaces it: said in words before it is sent.
  const changing = Boolean(g.ratified && chosen && chosen.id !== g.ratified.facility_id);
  // What to offer first: the building already confirmed (placing its waiting residents), else the history's suggestion.
  const primary = g.ratified
    ? { id: g.ratified.facility_id, name: g.ratified.facility_name || 'the confirmed building', words: 'Already confirmed: this places its residents with no building.' }
    : g.suggestion ? { id: g.suggestion.facility_id, name: g.suggestion.facility_name || 'the suggested building', words: suggestionWords(g) } : null;
  return (
    <div className="mf-panel nbc-set" role="group" aria-label={`Set ${label}'s building`}>
      <h4>Set {label}&apos;s building</h4>
      {!chosen && (
        <>
          {primary && (
            <p>
              <button type="button" className="board-btn" onClick={() => setChosen({ id: primary.id, name: primary.name })}>
                Use {primary.name}
              </button>{' '}
              <span className="nbc-quiet">{primary.words}</span>
            </p>
          )}
          <label className="nbc-search">
            <span>{primary ? 'Or find another eMerge building' : 'Find the eMerge building'}</span>
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Two or more letters of its name" aria-label="Find an eMerge building" />
          </label>
          {searchProblem && <p role="alert">{searchProblem}</p>}
          {hits && hits.length === 0 && <p className="nbc-quiet">No building matches.</p>}
          {hits && hits.length > 0 && (
            <ul className="nbc-hits" aria-label="Buildings found">
              {hits.map((h) => (
                <li key={h.id}>
                  <button type="button" className="board-btn ghost" onClick={() => setChosen({ id: h.id, name: h.name })}>
                    {h.name}{h.city ? ` · ${h.city}${h.state ? `, ${h.state}` : ''}` : ''}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p><button type="button" className="board-btn ghost" onClick={onCancel}>Cancel</button></p>
        </>
      )}
      {chosen && (
        <>
          <p>
            Set <strong>{label}</strong> as <strong>{chosen.name}</strong>?{' '}
            {plural(placeableOf(g), 'resident', 'residents')} of this clinic with no building will be placed there, whoever created them, and their PA will be the building&apos;s PA.
            {g.residents > placeableOf(g) ? ` ${g.residents - placeableOf(g)} more carry a building with no confirmed record and are not moved by this.` : ''}
            Residents who already have a building are not moved, nor anyone a person recorded another building for in eMerge.
            New residents of this clinic will be placed there too.
          </p>
          {changing && g.ratified && (
            <p role="note" className="nbc-warn">
              This changes {label}&apos;s confirmed building from <strong>{g.ratified.facility_name || 'its current building'}</strong> to <strong>{chosen.name}</strong>.
              Residents already placed at {g.ratified.facility_name || 'the current building'} are not moved.
            </p>
          )}
          {problem && <p role="alert">{problem}</p>}
          <p>
            <button type="button" className="board-btn" onClick={confirm} disabled={busy}>{busy ? 'Confirming…' : changing ? `Replace with ${chosen.name}` : `Confirm ${chosen.name}`}</button>{' '}
            <button type="button" className="board-btn ghost" onClick={() => setChosen(null)} disabled={busy}>Choose another</button>{' '}
            <button type="button" className="board-btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          </p>
        </>
      )}
    </div>
  );
}

function resultWords(r: ConfirmResult, building: Choice, g: ClinicGroup): string {
  const p = r.placement;
  const head = `${clinicLabel(g)} is ${building.name}.`;
  if (!p) return head;
  if (p.error) return `${head} The residents could not all be placed (${p.error}); set it again to place the rest.`;
  if (p.authority_refused) return `${head} No resident was placed: eMerge may not write a resident's building right now. Tell an administrator.`;
  const placed = `Placed ${plural(p.placed || 0, 'resident', 'residents')} there.`;
  return `${head} ${placed}${p.failed ? ` ${p.failed} could not be placed; set it again to retry.` : ''}`;
}

export default function NoBuildingClinics({ onChanged }: { onChanged?: () => void }) {
  const [data, setData] = useState<ClinicGroups | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [setting, setSetting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await noBuildingApi.clinics();
      if (r.success && r.data) setData(r.data);
      // A reviewer for some buildings only: the list below already says these residents are not theirs.
      else if (r.status === 403) setData(null);
      else setProblem(refusalWords(r, 'Could not load the clinics.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (problem) return <section className="nbc" aria-label="By CounselEAR clinic"><p role="alert">{problem}</p></section>;
  // Nothing to group and nothing to say: no section at all. A confirmation's outcome is still said after the last clinic goes.
  if (!data || data.enterprise_only || (data.items.length === 0 && !notice)) return null;

  return (
    <section className="nbc" aria-label="By CounselEAR clinic">
      <h3>By CounselEAR clinic</h3>
      <p className="nbc-quiet">
        A resident&apos;s CounselEAR Preferred Clinic is the facility they live at. Say once which eMerge building a clinic is, and its residents with no building are placed there.
      </p>
      {notice && <p role="status" className="nbc-notice">{notice}</p>}
      {data.items.length > 0 && (
      <table className="nbc-table">
        <thead>
          <tr><th scope="col">CounselEAR clinic</th><th scope="col">No building</th><th scope="col">Where its other residents live</th><th scope="col"><span className="pa-sr-only">Act</span></th></tr>
        </thead>
        <tbody>
          {data.items.map((g) => {
            const key = g.clinic_id || 'none';
            return (
              <React.Fragment key={key}>
                <tr>
                  <th scope="row">
                    {clinicLabel(g)}
                    {g.clinic_id && !g.clinic_name ? <span className="nbc-quiet"> (name not read from CounselEAR yet)</span> : null}
                    {g.enabled === false ? <span className="nbc-quiet"> · disabled in CounselEAR</span> : null}
                  </th>
                  <td>
                    {plural(g.residents, 'resident', 'residents')}
                    {g.from_monday ? <div className="nbc-quiet">{g.from_monday} of them created by Monday</div> : null}
                  </td>
                  <td>
                    {g.ratified
                      ? <span>Confirmed: {g.ratified.facility_name || 'a building'} · {placeableOf(g)} waiting to be placed</span>
                      : g.clinic_id ? suggestionWords(g) : 'CounselEAR named no clinic for these residents; a person places them one by one.'}
                    {g.proposed ? <div className="nbc-quiet">Proposed from history: {g.proposed.facility_name || 'a building'}</div> : null}
                  </td>
                  <td>
                    {data.can_confirm && g.clinic_id && setting !== key ? (
                      <button type="button" className="board-btn" onClick={() => { setNotice(null); setSetting(key); }}
                        aria-label={`Set ${clinicLabel(g)}'s building`}>
                        {g.ratified ? 'Place them' : 'Set this clinic\'s building'}
                      </button>
                    ) : null}
                  </td>
                </tr>
                {setting === key && (
                  <tr>
                    <td colSpan={4}>
                      <SetBuilding
                        g={g}
                        onCancel={() => setSetting(null)}
                        onStale={(words) => { setSetting(null); setNotice(words); load(); }}
                        onDone={(r, building) => { setSetting(null); setNotice(resultWords(r, building, g)); load(); if (onChanged) onChanged(); }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      )}
    </section>
  );
}
